import { Link, WhenVisible } from "@inertiajs/react";
import { LoaderCircle, Package, Plus } from "lucide-react";

import GroupGate from "@/components/group-gate";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { GROUPS } from "@/constants/groups";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import type { DeliveryNote } from "@/types";

interface DeliveryNotesTableProps {
  deliveryNotes?: DeliveryNote[];
  purchaseRequestId?: number;
}

export default function DeliveryNotesTable({
  deliveryNotes,
  purchaseRequestId,
}: DeliveryNotesTableProps) {
  const routes = useOrganizationRoutes();
  const addDeliveryNoteUrl = purchaseRequestId 
    ? routes.deliveryNotes.uploadNew({ purchase_request_id: purchaseRequestId })
    : routes.deliveryNotes.uploadNew();

  return (
    <GroupGate anyOf={[GROUPS.buyer, GROUPS.supervisor]}>
      <WhenVisible
        data="deliveryNotes"
        fallback={
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Remitos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <LoaderCircle className="h-8 w-8 mx-auto mb-4 animate-spin" />
                <p className="text-sm">Cargando remitos...</p>
              </div>
            </CardContent>
          </Card>
        }
      >
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Remitos
                </CardTitle>
                <CardDescription>
                  Remitos vinculados a esta solicitud de compra
                </CardDescription>
              </div>
              <Button size="sm" variant="outline" asChild>
                <Link href={addDeliveryNoteUrl}>
                  <Plus className="h-4 w-4 mr-1" />
                  Agregar remito
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {deliveryNotes && deliveryNotes.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Número de Remito</TableHead>
                    <TableHead>Fecha de Recepción</TableHead>
                    <TableHead>Proveedor</TableHead>
                    <TableHead>Cargado por</TableHead>
                    <TableHead>Revisado por</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deliveryNotes.map((note) => (
                    <TableRow key={note.id} className="cursor-pointer">
                      <TableCell>
                        <Link
                          href={routes.deliveryNotes.show(note)}
                          className="block w-full h-full font-medium"
                        >
                          #{note.id}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link
                          href={routes.deliveryNotes.show(note)}
                          className="block w-full h-full"
                        >
                          {note.number ?? "—"}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link
                          href={routes.deliveryNotes.show(note)}
                          className="block w-full h-full"
                        >
                          {note.receipt_date}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link
                          href={routes.deliveryNotes.show(note)}
                          className="block w-full h-full"
                        >
                          {note.supplier?.name ?? "Sin proveedor"}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link
                          href={routes.deliveryNotes.show(note)}
                          className="block w-full h-full"
                        >
                          {note.created_by?.full_name ?? "—"}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link
                          href={routes.deliveryNotes.show(note)}
                          className="block w-full h-full"
                        >
                          {note.reviewed_by?.full_name ?? "—"}
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Package />
                  </EmptyMedia>
                  <EmptyTitle>No hay remitos relacionados</EmptyTitle>
                  <EmptyDescription>
                    Los remitos aparecerán aquí cuando sean vinculados a esta
                    solicitud
                  </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button asChild variant="default" className="w-full sm:w-auto">
                      <Link href={addDeliveryNoteUrl}>
                        <Plus className="h-4 w-4 mr-1" />
                        Agregar remito
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="w-full sm:w-auto">
                      <Link href={routes.deliveryNotes.index()}>Ver todos los remitos</Link>
                    </Button>
                  </div>
                </EmptyContent>
              </Empty>
            )}
          </CardContent>
        </Card>
      </WhenVisible>
    </GroupGate>
  );
}
